import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useParams } from 'react-router-dom';
import { ReactComponent as SuccessIllustration } from 'assets/svg/success-illustration.svg';
import { ReactComponent as ErrorIllustration } from 'assets/svg/error-illustration.svg';
import { ReactComponent as FormIllustration } from 'assets/svg/form-illustration.svg';
import './AqroKredit.css';

const API_BASE = 'https://staging-api.yonca.az';

const MONTHS = Array.from({ length: 34 }, (_, i) => i + 3);

const VIEWS = {
  LOADING:   'loading',
  FORM:      'form',
  SUCCESS:   'success',
  DUPLICATE: 'duplicate',
  ERROR:     'error',
};

export const AqroKredit = () => {
  const { phone } = useParams();

  const [view, setView]         = useState(VIEWS.LOADING);
  const [amount, setAmount]     = useState('');
  const [duration, setDuration] = useState('');
  const [agreed, setAgreed]     = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [errors, setErrors]     = useState({});
  const [dropdownOpen, setDropdownOpen] = useState(false);
  const dropdownRef = useRef(null);

  const checkExisting = useCallback(async () => {
    try {
      const resp = await fetch(`${API_BASE}/agro-credit/applications/${encodeURIComponent(phone)}/check-status`);
      if (resp.status === 409) {
        setView(VIEWS.DUPLICATE);
      } else {
        setView(VIEWS.FORM);
      }
    } catch {
      setView(VIEWS.FORM);
    }
  }, [phone]);

  useEffect(() => { checkExisting(); }, [checkExisting]);

  useEffect(() => {
    if (!dropdownOpen) return;
    const close = (e) => {
      if (dropdownRef.current && !dropdownRef.current.contains(e.target))
        setDropdownOpen(false);
    };
    document.addEventListener('mousedown', close);
    return () => document.removeEventListener('mousedown', close);
  }, [dropdownOpen]);

  const validate = () => {
    const newErrors = {};
    const num = parseInt(amount.replace(/\D/g, ''), 10);
    if (!amount) {
      newErrors.amount = 'Kredit məbləğini daxil edin';
    } else if (isNaN(num) || num < 500 || num > 50000) {
      newErrors.amount = 'Məbləğ 500 – 50 000 AZN arasında olmalıdır';
    }
    if (!duration) newErrors.duration = 'Müddəti seçin';
    if (!agreed)   newErrors.agreed   = 'Razılığı təsdiq edin';
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async () => {
    if (!validate() || submitting) return;
    setSubmitting(true);
    try {
      const rawAmount = parseInt(amount.replace(/\D/g, ''), 10);
      const resp = await fetch(`${API_BASE}/agro-credit/applications/${encodeURIComponent(phone)}`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ amount: String(rawAmount), durationMonths: Number(duration) }),
      });
      if (resp.status === 409) { setView(VIEWS.DUPLICATE); return; }
      if (!resp.ok)            { setView(VIEWS.ERROR);     return; }
      setView(VIEWS.SUCCESS);
    } catch {
      setView(VIEWS.ERROR);
    } finally {
      setSubmitting(false);
    }
  };

  const handleAmountChange = (e) => {
    const raw = e.target.value.replace(/\D/g, '');
    setAmount(raw);
    if (errors.amount) setErrors(prev => ({ ...prev, amount: undefined }));
  };

  const displayAmount = amount
    ? parseInt(amount, 10).toLocaleString('ru-RU')
    : '';

  if (view === VIEWS.LOADING) {
    return (
      <div className="ak-page">
        <div className="ak-spinner" />
      </div>
    );
  }

  if (view === VIEWS.SUCCESS || view === VIEWS.DUPLICATE) {
    return (
      <div className="ak-page">
        <div className="ak-result-container">
          <div className="ak-check-circle">
            <svg width="28" height="22" viewBox="0 0 28 22" fill="none">
              <path d="M2 10L10 19L26 2" stroke="white" strokeWidth="3.5"
                strokeLinecap="round" strokeLinejoin="round"/>
            </svg>
          </div>
          <div className="ak-illustration">
            <SuccessIllustration />
          </div>
          <div className="ak-result-text">
            <h2 className="ak-result-title">
              {view === VIEWS.SUCCESS
                ? 'Müraciətiniz uğurla\nqeydə alındı'
                : 'Sizin kredit müraciətiniz qeydə alınmışdır'}
            </h2>
            {view === VIEWS.SUCCESS && (
              <p className="ak-result-subtitle">
                Tezliklə sizinlə əlaqə saxlanılacaq
              </p>
            )}
          </div>
        </div>
      </div>
    );
  }

  if (view === VIEWS.ERROR) {
    return (
      <div className="ak-page">
        <div className="ak-result-container">
          <div className="ak-illustration">
            <ErrorIllustration />
          </div>
          <h2 className="ak-error-title">Xəta baş verdi</h2>
          <button className="ak-btn ak-btn-primary" onClick={() => {
            setView(VIEWS.FORM);
            setErrors({});
          }}>
            Yenidən cəhd edin
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="ak-page">
      <div className="ak-form-container">
        <div className="ak-form-illustration">
          <FormIllustration />
        </div>
        <div className="ak-header">
          <h1 className="ak-title">Aqro kredit</h1>
          <p className="ak-subtitle">
            Təsərrüfat ehtiyacınız üçün məbləği və müddəti seçərək müraciət edə bilərsiniz
          </p>
        </div>

        <div className="ak-fields">
          <div className="ak-field-group">
            <label className="ak-label">
              <span className="ak-required">*</span>
              Kredit məbləği
            </label>
            <div className={`ak-input-wrapper${errors.amount ? ' ak-input-error' : ''}`}>
              <input
                type="text"
                inputMode="numeric"
                className="ak-input"
                placeholder="Daxil edin"
                value={displayAmount}
                onChange={handleAmountChange}
              />
              <span className="ak-input-suffix">AZN</span>
            </div>
            <span className="ak-hint">Min 500 - Max 50 000</span>
            {errors.amount && <span className="ak-error-msg">{errors.amount}</span>}
          </div>
          <div className="ak-field-group">
            <label className="ak-label">
              <span className="ak-required">*</span>
              Müddət
            </label>
            <div
              ref={dropdownRef}
              className={`ak-select-wrapper${errors.duration ? ' ak-input-error' : ''}${dropdownOpen ? ' ak-select-open' : ''}`}
              onClick={() => setDropdownOpen(o => !o)}
            >
              <div className="ak-select-display">
                <span className={duration ? 'ak-select-value' : 'ak-select-placeholder'}>
                  {duration ? `${duration} ay` : 'Seçim edin'}
                </span>
                <div className="ak-select-chevron">
                  <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
                    <path d="M4 6L8 10L12 6" stroke="#111827" strokeWidth="1.5"
                      strokeLinecap="round" strokeLinejoin="round"/>
                  </svg>
                </div>
              </div>
              {dropdownOpen && (
                <div className="ak-dropdown-menu">
                  {MONTHS.map(m => (
                    <div
                      key={m}
                      className={`ak-dropdown-option${duration === String(m) ? ' ak-dropdown-option-selected' : ''}`}
                      onClick={(e) => {
                        e.stopPropagation();
                        setDuration(String(m));
                        setDropdownOpen(false);
                        if (errors.duration) setErrors(prev => ({ ...prev, duration: undefined }));
                      }}
                    >
                      {m} ay
                    </div>
                  ))}
                </div>
              )}
            </div>
            {errors.duration && <span className="ak-error-msg">{errors.duration}</span>}
          </div>
          <div className="ak-checkbox-group">
            <label className="ak-checkbox-label">
              <input
                type="checkbox"
                className="ak-checkbox"
                checked={agreed}
                onChange={e => {
                  setAgreed(e.target.checked);
                  if (errors.agreed) setErrors(prev => ({ ...prev, agreed: undefined }));
                }}
              />
              <span className="ak-checkbox-text">
                Aqro fəaliyyətimə dair məlumatlardan istifadə edilməsinə etiraz etmirəm
              </span>
            </label>
            {errors.agreed && <span className="ak-error-msg">{errors.agreed}</span>}
          </div>
          <button
            className="ak-btn ak-btn-primary"
            onClick={handleSubmit}
            disabled={submitting}
          >
            {submitting ? 'Göndərilir...' : 'Göndər'}
          </button>

        </div>
      </div>
    </div>
  );
};
